import React, { useState, useRef } from 'react';
import emailjs from '@emailjs/browser';
import { trackPhoneClick, trackFormSubmit } from '../utils/gtag';
import {
  Typography,
  Container,
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Card,
  Chip,
  IconButton,
  Paper,
  Link as MuiLink,
} from '@mui/material';
import {
  Phone,
  Email,
  LocationOn,
  CheckCircle,
  TrendingUp,
  Calculate,
  Business,
  WhatsApp,
  VideoCall,
  LinkedIn,
  Facebook,
  Instagram,
} from '@mui/icons-material';
import { HEADER_HEIGHT } from '../App';

import Grid from '@mui/material/GridLegacy';
import PageHeader from '../components/PageHeader';
import { usePageMeta } from '../hooks/usePageMeta';

const ConsultationType = ({ icon, title, description, duration, selected, onClick }: any) => (
  <Card 
    sx={{ 
      p: 3, 
      cursor: 'pointer',
      transition: 'all 0.2s',
      borderRadius: 2,
      border: '2px solid',
      borderColor: selected ? 'secondary.main' : 'transparent',
      '&:hover': {
        borderColor: 'secondary.main',
        transform: 'scale(1.02)',
        boxShadow: 3
      }
    }}
    onClick={onClick}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
      {icon}
      <Typography variant="h6" sx={{ ml: 1, fontWeight: 600 }}>
        {title}
      </Typography>
    </Box>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      {description}
    </Typography>
    <Chip 
      label={duration}
      size="small"
      sx={{ backgroundColor: 'secondary.main', color: 'white' }}
    />
  </Card>
);

const Contact: React.FC = () => {
  usePageMeta(
    'Contact | Good Shepherd Accounting | Padstow, Sydney',
    'Book a free consultation with Good Shepherd Accounting in Padstow. Call 0425 660 773 or send a message and Nancy will reply personally.'
  );
  const formRef = useRef<HTMLFormElement>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    consultationType: '',
  });
  const [errors, setErrors] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [status, setStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({
    type: null,
    message: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleConsultationTypeSelect = (type: string) => {
    setFormData(prev => ({
      ...prev,
      consultationType: type,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({
      name: '',
      email: '',
      phone: '',
      message: '',
    });

    // Validate form
    let hasErrors = false;
    const newErrors = { ...errors };
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
      hasErrors = true;
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
      hasErrors = true;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
      hasErrors = true;
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone is required';
      hasErrors = true;
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Please tell us about your situation';
      hasErrors = true;
    }

    if (hasErrors) {
      setErrors(newErrors);
      setIsSubmitting(false);
      return;
    }

    try {
      const result = await emailjs.sendForm(
        'service_zaqqu1j',
        'template_jry3v3i',
        formRef.current!,
        'WP7_mfTmaGhkmRd04'
      );

      if (result.text === 'OK') {
        trackFormSubmit();
        setStatus({
          type: 'success',
          message: 'Thank you! I\'ll personally review your message and get back to you as soon as I can.',
        });
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: '',
          consultationType: '',
        });
      }
    } catch (error) {
      console.error('Error:', error);
      setStatus({
        type: 'error',
        message: 'There was an error sending your message. Please try calling (+61) 0425 660 773 directly.',
      });
    } finally {
      setIsLoading(false);
      setIsSubmitting(false);
    }
  };

  const consultationTypes = [
    {
      icon: <TrendingUp sx={{ color: 'secondary.main', fontSize: 28 }} />,
      title: "Business Growth Consultation",
      description: "Tax strategies, cash flow analysis, and growth planning",
      duration: "60 minutes",
    },
    {
      icon: <Calculate sx={{ color: 'secondary.main', fontSize: 28 }} />,
      title: "Tax Planning Session",
      description: "Minimise tax liability and maximise deductions",
      duration: "45 minutes",
    },
    {
      icon: <Business sx={{ color: 'secondary.main', fontSize: 28 }} />,
      title: "Quick Business Setup",
      description: "Structure advice, ABN registration, system setup",
      duration: "30 minutes",
    },
  ];

  return (
    <>
      <PageHeader
        title="Let's Talk"
        subtitle="Tell me about your situation and I'll personally get back to you."
      />
      <Box id="contact-content" sx={{ backgroundColor: '#fafafa', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="h3" component="h2" color="primary.main" gutterBottom sx={{ fontWeight: 600 }}>
              Step 1: Choose Your Consultation
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
              Select the consultation type that best fits your current needs
            </Typography>
            <Typography variant="body1" sx={{ mt: 2 }}>
              Prefer to talk?{' '}
              <MuiLink href="tel:+61425660773" onClick={trackPhoneClick} sx={{ fontWeight: 600 }}>
                Call 0425 660 773
              </MuiLink>
            </Typography>
          </Box>

          <Grid 
            container 
            spacing={3} 
            sx={{ 
              mb: 6,
              justifyContent: 'center',
              '& .MuiGridLegacy-item': {
                display: 'flex',
                flex: { xs: '0 0 100%', md: '0 0 350px' },
              },
              '& .MuiPaper-root': {
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
              }
            }}
          >
            {consultationTypes.map((type, index) => (
              <Grid item key={index}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    height: '100%',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    transition: 'all 0.2s ease-in-out',
                    cursor: 'pointer',
                    backgroundColor: formData.consultationType === type.title ? 'primary.main' : 'background.paper',
                    color: formData.consultationType === type.title ? 'white' : 'inherit',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: (theme) => theme.shadows[4],
                    },
                  }}
                  onClick={() => handleConsultationTypeSelect(type.title)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    {type.icon}
                    <Typography variant="h6" sx={{ ml: 1, fontWeight: 600 }}>
                      {type.title}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {type.description}
                  </Typography>
                  <Chip 
                    label={type.duration}
                    size="small"
                    sx={{ backgroundColor: 'secondary.main', color: 'white' }}
                  />
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ textAlign: 'center', mt: 10, mb: 8 }}>
            <Typography variant="h3" component="h2" color="primary.main" gutterBottom sx={{ fontWeight: 600 }}>
              Step 2: Book Your Consultation
            </Typography>
          </Box>

          <Container maxWidth="md">
            {formData.consultationType && (
              <Alert severity="info" sx={{ mb: 3 }}>
                Selected: {formData.consultationType}
              </Alert>
            )}

            {status.type && (
              <Alert severity={status.type} sx={{ mb: 3, borderRadius: 2 }}>
                {status.message}
              </Alert>
            )}

            <Box 
              component="form" 
              ref={formRef} 
              onSubmit={handleSubmit}
              sx={{
                width: '100%',
                maxWidth: '800px',
                mx: 'auto',
              }}
            >
              <Box 
                sx={{ 
                  mb: 3,
                  width: '100%',
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: { xs: 3, md: 2 },
                  justifyContent: 'space-between',
                }}
              >
                <TextField
                  required
                  fullWidth
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  error={!!errors.name}
                  helperText={errors.name}
                  sx={{
                    flex: { md: '0 1 32%' },
                  }}
                />
                <TextField
                  required
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  error={!!errors.email}
                  helperText={errors.email}
                  sx={{
                    flex: { md: '0 1 32%' },
                  }}
                />
                <TextField
                  required
                  fullWidth
                  label="Phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  error={!!errors.phone}
                  helperText={errors.phone}
                  sx={{
                    flex: { md: '0 1 32%' },
                  }}
                />
              </Box>

              <TextField
                required
                fullWidth
                multiline
                rows={4}
                label="Tell me about your current situation"
                name="message"
                value={formData.message}
                onChange={handleChange}
                error={!!errors.message}
                helperText={errors.message}
                sx={{ mb: 3 }}
              />

              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                fullWidth
                disabled={isLoading}
                sx={{
                  py: 1.5,
                  fontSize: '1.1rem',
                }}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Book My Free Consultation'
                )}
              </Button>
            </Box>
          </Container>
        </Container>
      </Box>
    </>
  );
};

export default Contact;